import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExternalEventService } from '@base/services/external-event.service';
import { LoadMFESrvAbstract } from '@base/abstracts/services/load-mfe.abstract';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { ModalService } from '@base/services/private/modal.service';
import { SessionService } from '@base/services/private/session.service';
import { TranslateService } from '@base/services/private/translate.service';
import { FaroService } from '@core/services/faro.service';
import { EventService } from '@base/services/private/event.service';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { getMfeAttributes, removeScript } from '@base/utils';
import { environment } from 'environments/environment';
import {
  InformaMfeEvent,
  InformaMfeMatchContext,
  InformaMfeNavigateValue,
} from '@base/models/others/informa-mfe-event';
import { RequestLabelService } from '@base/services/private/request-label.service';
import { MeetService } from '@base/services/private/meet.service';
import { Attendee } from '@base/models/responses/attendee.res';
import {
  handleInformaMfeUserAction,
  buildUserContext,
  mapHostLanguageToInforma,
  resolveTranslateEntity,
  resolveInformaMatchContext,
} from '@base/utils/informa-mfe.utils';
import { GlobalState } from '@base/models/others/global-state';
import { TypeOfRequestLabel } from '@base/enums/request-label.enum';
import { NavigationEnd, Router } from '@angular/router';
import { filter, lastValueFrom } from 'rxjs';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';

@Component({
  selector: 'app-modal-external-chat',
  templateUrl: './modal-external-chat.component.html',
  styleUrls: ['./modal-external-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslatePipe],
})
export class ModalExternalChatComponent implements AfterViewInit, OnDestroy {
  readonly modalId = input('');
  readonly askAnythingMessage = input('');
  readonly containerRef = viewChild<ElementRef>('containerRef');

  readonly isLoading = signal(true);
  readonly loadError = signal(false);

  private readonly externalEventSrv = inject(ExternalEventService);
  private readonly requestLabelSrv = inject(RequestLabelService);
  private readonly meetSrv = inject(MeetService);
  private readonly localBookmarks = new Set<string>();
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly modalSrv = inject(ModalService);
  private readonly faroSrv = inject(FaroService);
  private readonly sessionSrv = inject(SessionService);
  private readonly translateSrv = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly eventSrv = inject(EventService);

  private readonly destroyRef = inject(DestroyRef);
  private loadMfeSrv: LoadMFESrvAbstract | null = null;
  private mfeElement: HTMLElement | null = null;
  private mfeEventHandler: EventListener | null = null;
  private informaAuthToken = '';
  private globalState: GlobalState | null = this.globalStateSrv.getState();
  private mountTimer: ReturnType<typeof setTimeout> | null = null;
  private isDestroyed = false;

  /**
   * Closes the Informa concierge chat modal.
   *
   * @returns void
   */
  close(): void {
    this.modalSrv.close('modal-external-chat');
  }

  /**
   * Angular lifecycle hook — loads the MFE service then schedules the mount.
   *
   * @returns void
   */
  ngAfterViewInit(): void {
    (async () => {
      await this.initMfeSrv();
      this.syncMfeLanguage();
      this.scheduleMfeMount();
    })().catch((error) => {
      console.error('ModalExternalChat: init failed:', error);
      this.isLoading.set(false);
      this.loadError.set(true);
    });
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.close());
  }

  /**
   * Loads the whitelabel LoadMfe service and captures the current global state.
   *
   * @returns Promise<void>
   */
  private async initMfeSrv(): Promise<void> {
    this.globalState = this.globalStateSrv.getState();
    this.loadMfeSrv = await this.dynamicImportLoaderSrv.loadService<LoadMFESrvAbstract>(WhiteLabel.SERVICES.LoadMfe);
  }

  /**
   * Defers MFE mounting by 500 ms to allow the modal DOM to stabilise.
   *
   * @returns void
   */
  private scheduleMfeMount(): void {
    this.mountTimer = setTimeout(() => {
      this.mountTimer = null;
      if (this.isDestroyed) return;
      this.loadAndMount().catch((error) => {
        console.error('ModalExternalChat: mount failed:', error);
      });
    }, 500);
  }

  /**
   * Guards Informa group membership, loads the chat script and auth token,
   * then instances the MFE element. Sets isLoading/loadError signals throughout.
   *
   * @returns Promise<void>
   */
  private async loadAndMount(): Promise<void> {
    if (this.isDestroyed) return;
    this.isLoading.set(true);
    this.loadError.set(false);
    const pgId = this.globalState?.loader?.pg_id ?? '';
    const isInformaGroup = (this.loadMfeSrv?.externalMfeGroupIds ?? []).includes(pgId);
    if (!this.loadMfeSrv?.isExternalAIChat || !isInformaGroup) {
      this.isLoading.set(false);
      return;
    }
    if (this.isDestroyed) return;
    try {
      await this.loadMfeSrv.loadInformaChat();
    } catch (error) {
      console.error('ModalExternalChat: script load failed:', error);
      this.loadError.set(true);
      this.isLoading.set(false);
      return;
    }
    try {
      await this.loadInformaAuthToken();
    } catch {
      this.loadError.set(true);
      this.isLoading.set(false);
      return;
    }
    if (this.isDestroyed) return;
    this.instanceChatMfe();
    this.isLoading.set(false);
  }

  /**
   * Fetches a short-lived Informa auth token and caches it on the component.
   * Pushes any error to Faro before rethrowing so the caller can set loadError.
   *
   * @returns Promise<void>
   */
  private async loadInformaAuthToken(): Promise<void> {
    try {
      this.informaAuthToken = await this.externalEventSrv.getInformaToken();
    } catch (error) {
      this.faroSrv.pushError(error as Error);
      throw error;
    }
  }

  /**
   * Creates the `informa-concierge-module` custom element, sets all required
   * attributes, attaches the event listener and appends it to the container.
   *
   * @returns void
   */
  private instanceChatMfe(): void {
    const loader = this.globalState?.loader;
    const validate = this.globalState?.validate;
    if (!loader || !validate) {
      this.loadError.set(true);
      this.isLoading.set(false);
      return;
    }
    this.mfeElement = document.createElement('informa-concierge-module');
    Object.assign(this.mfeElement, { faroInstance: this.faroSrv.getInstance() });
    const { event_color, attendee_name } = loader;
    this.mfeElement.setAttribute('event-hash', environment.informaEventCode);
    this.mfeElement.setAttribute('auth-token', this.informaAuthToken);
    this.mfeElement.setAttribute('platform', 'WEB');
    this.mfeElement.setAttribute('lang', this.getMappedLanguage(validate.person_lang));
    this.mfeElement.setAttribute('event-color', event_color);
    this.mfeElement.setAttribute('api-base-url', environment.informaApiBaseUrl);
    this.mfeElement.setAttribute('floorplan-mfe-src', environment.informaFloorplan);
    if (attendee_name) {
      this.mfeElement.setAttribute('display-name', attendee_name);
    }
    if (this.askAnythingMessage()) {
      this.mfeElement.setAttribute('prompt', this.askAnythingMessage());
    }
    this.mfeElement.setAttribute('user-context', buildUserContext(this.localBookmarks));
    this.mfeEventHandler = this.onInformaChatEvent.bind(this) as EventListener;
    this.mfeElement.addEventListener('informaConciergeListener', this.mfeEventHandler);
    this.containerRef()?.nativeElement?.appendChild(this.mfeElement);
    const attrs = getMfeAttributes(this.mfeElement, ['auth-token']);
    this.faroSrv.pushMfeInit('informa-concierge-module', attrs);
  }

  private syncMfeLanguage(): void {
    this.translateSrv
      .getCurrentLanguage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((language) => {
        this.mfeElement?.setAttribute('lang', mapHostLanguageToInforma(language));
      });
  }

  private getMappedLanguage(authenticatedLanguage?: string): string {
    const hostLanguage = this.globalStateSrv.isAnon()
      ? (this.sessionSrv.getItem('lang') as string | null)
      : authenticatedLanguage;

    return mapHostLanguageToInforma(hostLanguage);
  }

  /**
   * Handles custom events emitted by the `informa-concierge-module` element.
   * Routes NAVIGATE to modal or router, refreshes token on AUTH_REQUIRED,
   * forwards ERROR to Faro, and closes modal on CLOSED.
   *
   * @param event - CustomEvent carrying an InformaMfeEvent payload.
   * @returns void
   */
  private onInformaChatEvent(event: CustomEvent<InformaMfeEvent>): void {
    const { type, value } = event.detail;
    console.info('Informa Chat MFE event:', { type });
    switch (type) {
      case 'NAVIGATE':
        this.handleInformaNavigate(value as InformaMfeNavigateValue);
        break;
      case 'AUTH_REQUIRED':
        void this.handleChatAuthRequired();
        break;
      case 'USER_ACTION':
        this.handleUserAction(value as Record<string, unknown>);
        break;
      case 'ERROR':
        console.error('InformaConciergeModule error:', { type });
        break;
      case 'CLOSED':
        this.close();
        break;
      case 'READY':
      case 'LOADING':
      case 'NATIVE_CAPABILITY_REQUEST':
        break;
      default:
        console.warn(`Unhandled Informa MFE event type: ${type}`);
    }
  }

  // start of custom cosmoprof-asia-2026: https://jublia.atlassian.net/browse/JO-XXXX
  private async handleChatAuthRequired(): Promise<void> {
    this.externalEventSrv.clearToken();
    try {
      await this.loadInformaAuthToken();
      this.mfeElement?.setAttribute('auth-token', this.informaAuthToken);
    } catch (err) {
      console.error('Informa chat token refresh failed:', err);
    }
  }

  /**
   * Dispatches NAVIGATE to entity-modal handler or route handler.
   * Replies via `navigate-result` in all cases — never a silent drop.
   */
  private handleInformaNavigate(nav: InformaMfeNavigateValue): void {
    const entity = resolveTranslateEntity(nav);
    if (entity && nav.entityId) {
      void this.handleChatEntityNavigate(nav, entity);
      return;
    }
    void this.handleChatRouteNavigate(nav);
  }

  private async handleChatEntityNavigate(nav: InformaMfeNavigateValue, entity: string): Promise<void> {
    const entityId = nav.entityId;
    if (!entityId) {
      this.replyNavigate(nav.requestId, 'FAILED', 'MISSING_ENTITY_ID');
      return;
    }

    const matchContext = resolveInformaMatchContext(nav.context);
    if (nav.target === 'AGENDA_SESSION') {
      let agendaHash: string;
      try {
        agendaHash = await this.externalEventSrv.translateExternalId('AGENDA', entityId);
      } catch (err) {
        console.error('Informa chat: translateExternalId for AGENDA_SESSION failed:', err);
        this.replyNavigate(nav.requestId, 'FAILED', 'EXTERNAL_ID_TRANSLATE_ERROR');
        return;
      }
      this.modalSrv.showModalAgendaDetail({
        addsData: { sessionId: agendaHash, ...(matchContext ? { matchContext } : {}) },
      });
      this.replyNavigate(nav.requestId, 'OPENED');
      return;
    }
    let entityHash: string;
    try {
      entityHash = await this.externalEventSrv.translateExternalId(entity, entityId);
    } catch (err) {
      console.error('Informa chat translate external id failed:', err);
      this.replyNavigate(nav.requestId, 'FAILED', 'EXTERNAL_ID_TRANSLATE_ERROR');
      return;
    }
    switch (nav.target) {
      case 'PEOPLE':
        this.modalSrv.showModalPeopleDetail({ addsData: { peopleId: entityHash } });
        this.replyNavigate(nav.requestId, 'OPENED');
        break;
      case 'EXHIBITOR':
        this.modalSrv.showModalExhibitorDetail({
          addsData: { exhibitorId: entityHash, ...(matchContext ? { matchContext } : {}) },
        });
        this.replyNavigate(nav.requestId, 'OPENED');
        break;
      case 'MEETING_REQUEST':
        if (nav.entityType === 'EXHIBITOR') {
          await this.handleExhibitorMeetingRequest(entityHash, nav.requestId, matchContext);
        } else {
          this.openRequestMeetModal(entityHash, nav.requestId);
        }
        break;
      case 'PRODUCT':
        this.modalSrv.showModalProductDetail({
          addsData: { productId: entityHash, ...(matchContext ? { matchContext } : {}) },
        });
        this.replyNavigate(nav.requestId, 'OPENED');
        break;
      default:
        console.warn(`Informa chat: unhandled entity target: ${nav.target}`);
        this.replyNavigate(nav.requestId, 'FAILED', 'UNSUPPORTED_TARGET');
    }
  }

  /**
   * Fetches the exhibitor's attendee list, then routes to:
   * - exactly 1 attendee: BM modal directly
   * - more than 1: people-list modal so the user picks who to meet
   * - 0 attendees: exhibitor detail (no one available to meet)
   */
  private async handleExhibitorMeetingRequest(
    companyHash: string,
    requestId: string,
    matchContext: InformaMfeMatchContext | null = null
  ): Promise<void> {
    let attendees: Attendee[];
    try {
      const res = await lastValueFrom(this.meetSrv.getCompanyDetail(companyHash));
      attendees = res.attendees ?? [];
    } catch (err) {
      console.error('Informa chat: getCompanyDetail failed:', err);
      this.replyNavigate(requestId, 'FAILED', 'COMPANY_DETAIL_ERROR');
      return;
    }
    if (attendees.length === 1) {
      this.openRequestMeetModal(attendees[0].id_attendee, requestId);
      return;
    }
    if (attendees.length > 1) {
      this.modalSrv.showModalPeopleList({ addsData: { attendees } });
      this.replyNavigate(requestId, 'OPENED');
      return;
    }
    this.modalSrv.showModalExhibitorDetail({
      addsData: { exhibitorId: companyHash, ...(matchContext ? { matchContext } : {}) },
    });
    this.replyNavigate(requestId, 'OPENED');
  }

  /** Opens the meeting-request modal and wires `navigate-result` replies to the MFE. */
  private openRequestMeetModal(peopleId: string, requestId: string): void {
    const meetRef = this.modalSrv.showModalRequestMeet({
      addsData: {
        peopleId,
        isMeetFromChat: false,
        action: TypeOfRequestLabel.REQUEST_MEETING,
        isSkipCheckMeeting: false,
        isCloseAll: false,
      },
      addsConfig: { modalId: 'modal-meet' },
    });
    const bm = meetRef?.componentInstance as { meetingRequestCompleted?: { subscribe(fn: () => void): void } };
    let meetCompleted = false;
    bm?.meetingRequestCompleted?.subscribe(() => {
      meetCompleted = true;
      this.replyNavigate(requestId, 'COMPLETED');
    });
    meetRef?.closed.subscribe(() => {
      if (!meetCompleted) this.replyNavigate(requestId, 'DISMISSED');
    });
    meetRef?.dismissed.subscribe(() => this.replyNavigate(requestId, 'DISMISSED'));
  }

  private async navigateAndAck(route: string[], requestId: string): Promise<void> {
    try {
      const navigated = await this.router.navigate(route);
      if (!navigated) {
        this.replyNavigate(requestId, 'FAILED', 'NAVIGATION_CANCELLED');
        return;
      }
      this.replyNavigate(requestId, 'COMPLETED');
      this.close();
    } catch (error) {
      console.error('Informa chat: route navigation failed:', error);
      this.replyNavigate(requestId, 'FAILED', 'NAVIGATION_ERROR');
    }
  }

  private async handleChatRouteNavigate(nav: InformaMfeNavigateValue): Promise<void> {
    const eventDir = this.eventSrv?.data?.name;
    const base = this.eventSrv?.data?.isBrandedDomain ? '/' : (eventDir ?? '/');
    switch (nav.target) {
      case 'DIRECTORY': {
        const routeMap: Record<string, string[]> = {
          EXHIBITOR: [base, 'meet', 's', 'companies'],
          PEOPLE: [base, 'meet', 's', 'people'],
          PRODUCT: [base, 'meet', 's', 'products'],
        };
        const route = routeMap[nav.context?.entityType ?? ''] ?? [base, 'meet', 's', 'companies'];
        await this.navigateAndAck(route, nav.requestId);
        break;
      }
      case 'BOOKMARKS':
        await this.navigateAndAck([base, 'planner', 'bookmark'], nav.requestId);
        break;
      case 'MY_SCHEDULE':
        await this.navigateAndAck([base, 'planner', 'me'], nav.requestId);
        break;
      case 'REQUEST_SENT':
        await this.navigateAndAck([base, 'meet', 'sent'], nav.requestId);
        break;
      default:
        console.warn(`Informa chat: unhandled NAVIGATE target: ${nav.target}`);
        this.replyNavigate(nav.requestId, 'FAILED', 'UNSUPPORTED_TARGET');
    }
  }

  private async handleUserAction(payload: Record<string, unknown>): Promise<void> {
    try {
      await handleInformaMfeUserAction(payload, {
        localBookmarks: this.localBookmarks,
        externalEventSrv: this.externalEventSrv,
        requestLabelSrv: this.requestLabelSrv,
        logPrefix: 'Informa chat',
      });
      this.mfeElement?.setAttribute('user-context', buildUserContext(this.localBookmarks));
    } catch (error) {
      console.error('Informa chat: bookmark update failed:', error);
    }
  }

  /**
   * Sets the `navigate-result` attribute on the MFE element to acknowledge a NAVIGATE event.
   * Errors during serialisation are caught and logged — never a crash.
   * Unknown `requestId` values are echoed back as-is; the MFE is responsible for
   * ignoring replies it does not recognise.
   *
   * @param requestId - Echo of the `requestId` from the incoming NAVIGATE event.
   * @param outcome - Terminal outcome: OPENED, COMPLETED, DISMISSED, or FAILED.
   * @param errorCode - Stable error code, present only on FAILED outcomes.
   */
  private replyNavigate(
    requestId: string,
    outcome: 'OPENED' | 'COMPLETED' | 'DISMISSED' | 'FAILED',
    errorCode?: string
  ): void {
    if (!this.mfeElement || !requestId) return;
    try {
      const result: Record<string, string> = { requestId, outcome };
      if (errorCode) result['errorCode'] = errorCode;
      this.mfeElement.setAttribute('navigate-result', JSON.stringify(result));
    } catch {
      console.error('Informa chat: failed to set navigate-result attribute');
    }
  }
  // end of custom cosmoprof-asia-2026: https://jublia.atlassian.net/browse/JO-XXXX

  /**
   * Removes the existing MFE element from the DOM and re-runs loadAndMount.
   * Called by the retry button when isLoading or loadError is shown.
   *
   * @returns void
   */
  retryLoad(): void {
    if (this.mfeElement && this.containerRef()?.nativeElement?.contains(this.mfeElement)) {
      this.containerRef()?.nativeElement?.removeChild(this.mfeElement);
    }
    this.mfeElement = null;
    (async () => {
      await this.initMfeSrv();
      await this.loadAndMount();
    })().catch((error) => {
      console.error('ModalExternalChat: retry failed:', error);
      this.isLoading.set(false);
      this.loadError.set(true);
    });
  }

  /**
   * Angular lifecycle hook — removes the MFE event listener, nulls the element
   * reference and unloads the chat script to prevent memory leaks.
   *
   * @returns void
   */
  ngOnDestroy(): void {
    this.isDestroyed = true;
    if (this.mountTimer !== null) {
      clearTimeout(this.mountTimer);
      this.mountTimer = null;
    }
    if (this.mfeElement && this.mfeEventHandler) {
      this.mfeElement.removeEventListener('informaConciergeListener', this.mfeEventHandler);
      this.mfeEventHandler = null;
    }
    this.mfeElement = null;
    removeScript(environment.informaChat);
  }
}
